import { useFeatureDetectionContext } from "@/components/common/feature-detection-context";
import { useZoomLensClip } from "@/components/r3f/renderers/measurements/zoom-lens-clip";
import { useAppDispatch } from "@/store/store-hooks";
import { useEffect } from "react";
import {
  MultiPointMeasureControlsLogic,
  ProjectionType,
} from "./multi-point-measures-controls-logic";

/**
 * This hook is used by the MultiPointMeasureControls component, to ensure that the controls
 * of the 3D measurements receive keyboard events.
 *
 * @param controls  The object responsible for the models' measurement logic
 * @param onDeleteActiveMeasurement Callback issued when the user deletes the active measurement
 * @param onEscPressed Callback issued when the user presses the Escape key
 * @param projectOnShiftKey The projection type to apply when the Shift key is pressed
 * @param onChangeProjectionType Callback issued when the projection type changes
 */
export function useForwardKeyEventsToControls(
  controls: MultiPointMeasureControlsLogic,
  onDeleteActiveMeasurement: () => void,
  onEscPressed: (() => void) | undefined,
  projectOnShiftKey: ProjectionType,
  onChangeProjectionType?: (projectionType: ProjectionType) => void,
): void {
  const dispatch = useAppDispatch();

  const zoom = useZoomLensClip();

  const { setEdgeCornerDetection, features } = useFeatureDetectionContext();

  // Bind the escape key to cancel the current measurement
  // And the Delete key to delete the active measurement
  // Making sure to unbind on unmount or when controls change, before binding to the new controls.
  useEffect(() => {
    // eslint-disable-next-line func-style -- FIXME
    const handleKeyDown = (event: KeyboardEvent): void => {
      switch (event.key) {
        case "Escape":
          if (!controls.points || controls.points.length <= 1) {
            // delete last picked point if any...
            if (!controls.deleteLatestPoint()) {
              // ...or cancel the measurement if no points are picked
              controls.cancel();
            }
          } else {
            // for 2 or more picked points, Escape key completes the measurement as open polygon
            controls.complete(false);
          }
          break;
        case "Delete":
          if (!controls.isLiveMeasure()) {
            // Delete key does delete the active measurement when not creating a new measurement
            onDeleteActiveMeasurement();
          }
          break;
        case "Backspace":
          if (controls.isLiveMeasure()) {
            // Backspace key does delete the latest picked point when creating a new measurement
            controls.deleteLatestPoint();
          }
          break;
        case "Shift":
          controls.enableProjection(projectOnShiftKey);
          onChangeProjectionType?.(projectOnShiftKey);
          zoom.projectionTypeRef.current = projectOnShiftKey;
          break;
        case "Meta":
        case "Control":
          setEdgeCornerDetection(true);
          break;
      }
    };
    // eslint-disable-next-line func-style -- FIXME
    const handleKeyUp = (event: KeyboardEvent): void => {
      switch (event.key) {
        case "Shift":
          controls.enableProjection(ProjectionType.DoNotProject);
          onChangeProjectionType?.(ProjectionType.DoNotProject);
          zoom.projectionTypeRef.current = ProjectionType.DoNotProject;
          break;
        case "Meta":
        case "Control":
          setEdgeCornerDetection(false);
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    controls,
    onDeleteActiveMeasurement,
    onEscPressed,
    dispatch,
    projectOnShiftKey,
    onChangeProjectionType,
    setEdgeCornerDetection,
    features,
    zoom,
  ]);
}
